import React, {FC, ReactNode, useState} from "react";
import moment from "moment";
import {Button, Radio, Tooltip} from "antd";
import {blue} from "@ant-design/colors";
import {renderAmount} from "../../../helpers/renderAmount";
import {colorPrimaryBackground} from "../../../styles/colors";
import {ReportCompare, ReportDashboardQuery, ReportPeriod} from "../../../generated/graphql";
import css from "./Reports.less";
import {compareLabels, formatInt, monthLabels, shareLabel, shortDay, sourceLabel, weekdayLabels} from "./labels";
import {StatLabel} from "./help";

export type Dashboard = ReportDashboardQuery["reportDashboard"];

type Ctx = {
    data: Dashboard;
    period: ReportPeriod;
    compare: ReportCompare;
};

// Datenfarbe durchgehend Primärblau (Entscheidung 04.11.2026: Variante A mit blauen Daten).
export const ACCENT = colorPrimaryBackground;
const RAMP = [blue[1], blue[2], blue[4], blue[6], blue[8]];
const COMPARE_COLOR = "rgba(0,0,0,0.25)";

const eur = (value: number) => renderAmount({amount: Math.round(value), short: true});

// ---------------------------------------------------------------- Bausteine

export const Delta: FC<{value: number | null; unit?: string; lowerIsBetter?: boolean; noCompare?: boolean}> = ({value, unit = "", lowerIsBetter = false, noCompare}) => {
    if (noCompare) {
        return (
            <span className={`${css.delta} ${css.flat}`} title="Für den Vergleichszeitraum liegen noch keine Zahlen vor">
                {"–"}
            </span>
        );
    }
    if (value === null) {
        return <span className={`${css.delta} ${css.flat}`}>{"neu"}</span>;
    }
    const good = lowerIsBetter ? value < 0 : value > 0;
    const cls = value === 0 ? css.flat : good ? css.up : css.down;
    const glyph = value === 0 ? "±" : value > 0 ? "▲" : "▼";

    return (
        <span className={`${css.delta} ${cls}`}>
            {glyph} {value > 0 ? "+" : ""}
            {formatInt(value)}
            {unit}
        </span>
    );
};

/** Prozentuale Veränderung; null, wenn es keinen Vergleichswert gibt. */
export const pctChange = (current: number, previous: number): number | null => (previous === 0 ? (current === 0 ? 0 : null) : Math.round(((current - previous) / previous) * 100));

const Hero: FC<{value: string; unit?: string; label: ReactNode; delta?: ReactNode}> = ({value, unit, label, delta}) => (
    <div>
        <div className={css.hero}>
            <span className={css.heroValue}>{value}</span>
            {unit ? <span className={css.heroUnit}>{unit}</span> : null}
            {delta}
        </div>
        <div className={css.heroLabel}>{label}</div>
    </div>
);

const Empty: FC<{text: string}> = ({text}) => <div className={css.empty}>{text}</div>;

/** Liegen für das Vergleichsfenster Fakten vor? Vor der Rückfüllung wären sonst alle Deltas „neu“. */
const hasCompareData = (data: Dashboard) => Boolean(data.dataFrom) && data.compareFrom >= data.dataFrom!;

const Stat: FC<{label: string; value: ReactNode; delta?: ReactNode}> = ({label, value, delta}) => (
    <div className={css.stat}>
        <div className={css.statLabel}>
            <StatLabel label={label} />
        </div>
        <div className={css.statValue}>{value}</div>
        {delta}
    </div>
);

/** Tag vor einem YYYY-MM-DD-Tag. */
const dayBefore = (day: string) => new Date(new Date(`${day}T00:00:00Z`).getTime() - 86400000).toISOString().slice(0, 10);

// ---------------------------------------------------------------- 1 Potenzial

type PotentialMode = "month" | "course";
// Mehr Standorte klappt die Liste auf Wunsch auf; sonst wächst die Kachel bei Ketten (45 Standorte) ins Unermessliche.
const TOP_LOCATIONS = 10;

export const PotentialTile: FC<Ctx> = ({data}) => {
    const [mode, setMode] = useState<PotentialMode>("month");
    const [showAll, setShowAll] = useState(false);
    const p = data.potential;
    const value = (row: {perMonth: number; perCourse: number}) => (mode === "month" ? row.perMonth : row.perCourse);
    const locations = [...p.byLocation].sort((a, b) => value(b) - value(a));
    const visible = showAll ? locations : locations.slice(0, TOP_LOCATIONS);
    const max = Math.max(...locations.map(value), 1);

    return (
        <>
            <div className={css.heroRow}>
                <Hero
                    value={eur(value(p))}
                    unit={mode === "month" ? "pro Monat ungenutzt" : "je Kurslaufzeit ungenutzt"}
                    label={`${formatInt(p.freePlaces)} freie Plätze in ${formatInt(p.courses)} laufenden und bald startenden Kursen`}
                />
                <Radio.Group size="small" value={mode} onChange={(e) => setMode(e.target.value)}>
                    <Radio.Button value="month">{"je Monat"}</Radio.Button>
                    <Radio.Button value="course">{"je Laufzeit"}</Radio.Button>
                </Radio.Group>
            </div>
            {p.byLocation.length === 0 ? <Empty text="Alle Plätze belegt oder keine laufenden Kurse mit Preis." /> : null}
            {locations.length > 1 ? (
                <div className={`${css.bars} ${showAll ? css.barsScroll : ""}`}>
                    {visible.map((row) => (
                        <div key={row.locationId} className={css.barRow}>
                            <span className={css.barLabel} title={row.locationName}>
                                {row.locationName}
                            </span>
                            <div className={css.barTrack}>
                                <div className={css.barFill} style={{width: `${(value(row) / max) * 100}%`}} />
                            </div>
                            <span className={css.barValue}>
                                {eur(value(row))}
                                <span className={css.barMeta}>
                                    {formatInt(row.freePlaces)}
                                    {" Plätze"}
                                </span>
                            </span>
                        </div>
                    ))}
                </div>
            ) : null}
            {locations.length > TOP_LOCATIONS ? (
                <Button type="link" size="small" className={css.moreLink} onClick={() => setShowAll(!showAll)}>
                    {showAll ? `Nur die ${TOP_LOCATIONS} größten Standorte` : `Alle ${locations.length} Standorte anzeigen`}
                </Button>
            ) : null}
            {p.topCourses.length > 0 ? (
                <div className={css.list}>
                    <div className={css.listSub} style={{padding: "10px 0 2px"}}>
                        {"Größter Hebel"}
                    </div>
                    {[...p.topCourses]
                        .sort((a, b) => value(b) - value(a))
                        .slice(0, 4)
                        .map((c) => (
                            <div key={c.courseId} className={css.listRow}>
                                <div>
                                    <div className={css.listMain}>
                                        {c.courseNumber} {c.courseName}
                                    </div>
                                    <div className={css.listSub}>
                                        {c.locationName}
                                        {", ab "}
                                        {moment(c.startDateTime).format("DD.MM.")}
                                    </div>
                                </div>
                                <span className={css.listValue}>
                                    {eur(value(c))}
                                    <span className={css.barMeta}>
                                        {c.freePlaces}
                                        {" frei"}
                                    </span>
                                </span>
                            </div>
                        ))}
                </div>
            ) : null}
            <div className={css.hint}>
                <span className={css.hintDot} style={{background: "rgba(0,0,0,0.25)"}} />
                <span>{"Freie Plätze mal Kurspreis ohne Rabatte, Stand jetzt. Einzelbuchungskurse zählen nicht mit."}</span>
            </div>
        </>
    );
};

// ---------------------------------------------------------------- 2 Heatmap

const rampColor = (pct: number) => {
    if (pct >= 100) return RAMP[4];
    if (pct >= 90) return RAMP[3];
    if (pct >= 70) return RAMP[2];
    if (pct >= 40) return RAMP[1];

    return RAMP[0];
};

export const HeatmapTile: FC<Ctx> = ({data}) => {
    const slots = data.slots;

    if (slots.length === 0) {
        return <Empty text="Keine Einheiten im Zeitraum." />;
    }
    const hours = Array.from({length: 24}, (_, h) => h).filter((h) => h >= Math.min(8, ...slots.map((s) => s.hour)) && h <= Math.max(19, ...slots.map((s) => s.hour)));
    const byKey = new Map(slots.map((s) => [`${s.weekday}-${s.hour}`, s]));
    const totals = slots.reduce((acc, s) => ({lessons: acc.lessons + s.lessons, capacity: acc.capacity + s.capacity, occupied: acc.occupied + s.occupied}), {lessons: 0, capacity: 0, occupied: 0});
    const rate = (s: Dashboard["slots"][number]) => s.occupied / Math.max(1, s.capacity);
    // Nur Slots mit mindestens zwei Einheiten, damit ein einzelner Kurs nicht die Extreme stellt
    const ranked = slots.filter((s) => s.capacity > 0 && (s.lessons >= 2 || slots.length < 6)).sort((a, b) => rate(b) - rate(a));
    const fullest = ranked[0];
    const emptiest = ranked[ranked.length - 1];
    const slotLabel = (s?: Dashboard["slots"][number]) => (s ? `${weekdayLabels[s.weekday - 1]} ${s.hour} Uhr, ${Math.min(100, Math.round(rate(s) * 100))} %` : "–");

    return (
        <>
            <div className={css.heat} style={{gridTemplateColumns: `28px repeat(${hours.length}, minmax(0, 1fr))`}}>
                <span />
                {hours.map((h) => (
                    <span key={h} className={css.heatAxis}>
                        {h}
                    </span>
                ))}
                {weekdayLabels.map((day, d) => (
                    <React.Fragment key={day}>
                        <span className={css.heatDay}>{day}</span>
                        {hours.map((h) => {
                            const slot = byKey.get(`${d + 1}-${h}`);

                            if (!slot || slot.capacity === 0) {
                                return <div key={h} className={`${css.heatCell} ${css.heatEmpty}`} title={`${day} ${h} Uhr: keine Einheit`} />;
                            }
                            const pct = Math.min(100, Math.round((slot.occupied / slot.capacity) * 100));

                            return (
                                <Tooltip
                                    key={h}
                                    title={`${day} ${h} Uhr: ${pct} % belegt (${formatInt(slot.occupied)} von ${formatInt(slot.capacity)} Plätzen, ${slot.lessons} Einheiten)`}
                                    mouseEnterDelay={0.05}
                                >
                                    <div className={css.heatCell} style={{background: rampColor(pct)}} />
                                </Tooltip>
                            );
                        })}
                    </React.Fragment>
                ))}
            </div>
            <div className={css.heatLegend}>
                <span>{"frei"}</span>
                <span className={css.heatRamp} style={{background: `linear-gradient(90deg, ${RAMP[0]}, ${RAMP[2]}, ${RAMP[4]})`}} />
                <span>{"voll"}</span>
                <span style={{marginLeft: 12}}>{"grau = keine Einheit"}</span>
            </div>
            <div className={css.statRow} style={{marginTop: 16}}>
                <Stat label="Belegung gesamt" value={`${Math.min(100, Math.round((totals.occupied / Math.max(1, totals.capacity)) * 100))} %`} />
                <Stat label="Vollste Stunde" value={slotLabel(fullest)} />
                <Stat label="Freieste Stunde" value={slotLabel(emptiest)} />
            </div>
            <div className={css.hint}>
                <span className={css.hintDot} style={{background: "rgba(0,0,0,0.25)"}} />
                <span>
                    {formatInt(totals.lessons)}
                    {" Einheiten mit "}
                    {formatInt(totals.capacity)} {"Plätzen im Zeitraum, geplante Einheiten eingeschlossen. Stunden ohne Einheit bleiben grau."}
                </span>
            </div>
        </>
    );
};

// ---------------------------------------------------------------- 3 Vergleich

/** Runde Obergrenze für die Achse: 1, 2, 5 × Zehnerpotenz. */
const niceCeiling = (max: number): number => {
    if (max <= 0) return 1;
    const magnitude = 10 ** Math.floor(Math.log10(max));
    const steps = [1, 2, 5, 10];
    const step = steps.find((s) => s * magnitude >= max) ?? 10;

    return step * magnitude;
};

const shortK = (value: number) => (value >= 1000 ? `${(value / 1000).toLocaleString("de-DE", {maximumFractionDigits: 1})}k` : String(Math.round(value)));

/** Ein Monat ist abgedeckt, wenn die Rückfüllung spätestens an seinem ersten Tag beginnt. */
const monthCovered = (month: string, dataFrom?: string | null) => Boolean(dataFrom) && `${month}-01` >= dataFrom!;
const yearBefore = (month: string) => `${parseInt(month.slice(0, 4), 10) - 1}${month.slice(4)}`;

type LineChartProps = {
    series: Dashboard["series"];
    dataFrom?: string | null;
    /** Wertformat für Tooltip und Achse; Standard Euro. */
    format?: (value: number) => string;
    axis?: (value: number) => string;
    label?: string;
    /** Feste Obergrenze der Achse (Prozent: 100) statt der nächsten runden Zahl über dem Maximum. */
    max?: number;
};

const LineChart: FC<LineChartProps> = ({series, dataFrom, format = eur, axis = shortK, label = "Zahlungseingang je Monat, aktuell gegen Vorjahr", max: fixedMax}) => {
    const width = 600;
    const height = 170;
    const left = 36;
    const bottom = 20;
    const top = 8;
    const plotW = width - left - 8;
    const plotH = height - top - bottom;
    const max = fixedMax ?? niceCeiling(Math.max(...series.map((s) => Math.max(s.current, s.previous)), 1));
    const x = (i: number) => left + (plotW / Math.max(1, series.length - 1)) * i;
    const y = (v: number) => top + plotH - (v / max) * plotH;
    const covered = (s: Dashboard["series"][number], key: "current" | "previous") => monthCovered(key === "current" ? s.month : yearBefore(s.month), dataFrom);
    // Nicht rückgefüllte Monate bleiben Lücken statt Nullen
    const path = (key: "current" | "previous") =>
        series
            .map((s, i, all) => (covered(s, key) ? `${i === 0 || !covered(all[i - 1], key) ? "M" : "L"}${x(i).toFixed(1)},${y(s[key]).toFixed(1)}` : ""))
            .filter(Boolean)
            .join(" ");
    const amount = (s: Dashboard["series"][number], key: "current" | "previous") => (covered(s, key) ? format(s[key]) : "keine Daten");
    const ticks = [0, 0.25, 0.5, 0.75, 1].map((f) => f * max);

    return (
        <svg className={css.lineChart} viewBox={`0 0 ${width} ${height}`} preserveAspectRatio="none" role="img" aria-label={label}>
            {ticks.map((t) => (
                <g key={t}>
                    <line x1={left} x2={width - 8} y1={y(t)} y2={y(t)} stroke="rgba(0,0,0,0.06)" />
                    <text x={left - 6} y={y(t) + 3} textAnchor="end" className={css.axisText}>
                        {axis(t)}
                    </text>
                </g>
            ))}
            {series.map((s, i) => (
                <text key={s.month} x={x(i)} y={height - 4} textAnchor="middle" className={css.axisText}>
                    {monthLabels[parseInt(s.month.slice(5, 7), 10) - 1]}
                </text>
            ))}
            <path d={path("previous")} fill="none" stroke={COMPARE_COLOR} strokeWidth={2} vectorEffect="non-scaling-stroke" />
            <path d={path("current")} fill="none" stroke={ACCENT} strokeWidth={2} vectorEffect="non-scaling-stroke" />
            {series.map((s, i) => (
                <Tooltip
                    key={s.month}
                    title={`${monthLabels[parseInt(s.month.slice(5, 7), 10) - 1]} ${s.month.slice(0, 4)}: ${amount(s, "current")}, Vorjahr ${amount(s, "previous")}`}
                    mouseEnterDelay={0.05}
                >
                    <g className={css.chartPoint}>
                        <rect x={x(i) - plotW / series.length / 2} y={top} width={plotW / series.length} height={plotH} fill="transparent" />
                        {covered(s, "current") ? <circle cx={x(i)} cy={y(s.current)} r={3} fill={ACCENT} stroke="#fff" strokeWidth={1.5} vectorEffect="non-scaling-stroke" /> : null}
                    </g>
                </Tooltip>
            ))}
        </svg>
    );
};

export const CompareTile: FC<Ctx> = ({data, compare}) => {
    const vs = compareLabels[compare];
    const compareData = hasCompareData(data);
    // Vergleichstage mit Jahreszahl, sobald sie in einem anderen Jahr liegen als der Zeitraum
    const compareDay = (day: string) => (day.slice(0, 4) === data.from.slice(0, 4) ? shortDay(day) : `${shortDay(day)}${day.slice(0, 4)}`);
    const stats: Array<{label: string; current: number; previous: number; money?: boolean; lowerIsBetter?: boolean}> = [
        {label: "Buchungen", current: data.current.bookings, previous: data.previous.bookings},
        {label: "Buchungswert", current: data.current.bookingVolume, previous: data.previous.bookingVolume, money: true},
        {label: "Zahlungseingang", current: data.current.revenuePaid, previous: data.previous.revenuePaid, money: true},
        {label: "Stornos", current: data.current.cancellations, previous: data.previous.cancellations, lowerIsBetter: true},
        {label: "Neue Kunden", current: data.current.newBookers, previous: data.previous.newBookers},
    ];

    return (
        <>
            <div className={css.legend}>
                <span>
                    <span className={css.legendKey} style={{background: ACCENT}} />
                    {"Zahlungseingang je Monat"}
                </span>
                <span>
                    <span className={css.legendKey} style={{background: COMPARE_COLOR}} />
                    {"Vorjahr"}
                </span>
            </div>
            <div className={css.chart}>
                <LineChart series={data.series} dataFrom={data.dataFrom} />
            </div>
            <div className={css.statRow}>
                {stats.map((s) => (
                    <Stat
                        key={s.label}
                        label={s.label}
                        value={s.money ? eur(s.current) : formatInt(s.current)}
                        delta={<Delta value={pctChange(s.current, s.previous)} unit=" %" lowerIsBetter={s.lowerIsBetter} noCompare={!compareData} />}
                    />
                ))}
            </div>
            <div className={css.hint}>
                <span className={css.hintDot} style={{background: "rgba(0,0,0,0.25)"}} />
                <span>
                    {data.from === data.to
                        ? "Noch kein abgeschlossener Tag im Zeitraum. "
                        : `Kennzahlen ${shortDay(data.from)} bis ${shortDay(dayBefore(data.to))} gegen ${vs} (${compareDay(data.compareFrom)} bis ${compareDay(dayBefore(data.compareTo))}). `}
                    {compareData ? "" : "Für den Vergleichszeitraum liegen noch keine Zahlen vor. "}
                    {"Buchungswert nach Buchungsdatum, Zahlungseingang nach Zahlungsdatum."}
                </span>
            </div>
        </>
    );
};

// ---------------------------------------------------------------- 4 Herkunft

export const SourcesTile: FC<Ctx> = ({data, compare}) => {
    const sources = data.sources;
    const total = sources.reduce((sum, s) => sum + s.bookings, 0);

    if (total === 0) {
        return <Empty text="Keine Buchungen im Zeitraum." />;
    }
    const max = Math.max(...sources.map((s) => s.bookings), 1);
    const compareData = hasCompareData(data);
    const count = (key: string) => sources.find((s) => s.source === key)?.bookings ?? 0;
    const share = (n: number) => shareLabel(n, total);
    const online = total - count("staff");

    return (
        <>
            <div className={css.heroLabel} style={{marginTop: 0}}>
                {formatInt(total)}
                {" Buchungen, Anteil je Quelle. Veränderung gegenüber "}
                {compareLabels[compare]}
                {"."}
            </div>
            <div className={css.statRow}>
                <Stat label="Online gebucht" value={share(online)} />
                <Stat label="Vom Büro gebucht" value={share(count("staff"))} />
                <Stat label="Mit bekannter Quelle" value={share(online - count("direct"))} />
            </div>
            <div className={css.bars}>
                {sources
                    .filter((s) => s.bookings > 0)
                    .map((s) => (
                        <div key={s.source} className={css.barRow} style={{gridTemplateColumns: "96px 1fr 44px 70px"}}>
                            <span className={css.barLabel} title={s.source}>
                                {sourceLabel(s.source)}
                            </span>
                            <div className={css.barTrack}>
                                <div className={css.barFill} style={{width: `${(s.bookings / max) * 100}%`}} />
                            </div>
                            <span className={css.barValue} title={s.bookings === 1 ? "1 Buchung" : `${formatInt(s.bookings)} Buchungen`}>
                                {share(s.bookings)}
                            </span>
                            <Delta value={pctChange(s.bookings, s.previousBookings)} unit=" %" noCompare={!compareData} />
                        </div>
                    ))}
            </div>
            <div className={css.hint}>
                <span className={css.hintDot} style={{background: "rgba(0,0,0,0.25)"}} />
                <span>{"„Direkt“ sind Online-Buchungen ohne Herkunftsangabe, „Büro“ Buchungen durch das Team. Quellen aus utm_source der Buchungslinks."}</span>
            </div>
        </>
    );
};

// ---------------------------------------------------------------- 5 Wiederbucher

/** Anteil in ganzen Prozent; 0 ohne Buchungen (gleiche Regel wie repeatShare im Resolver). */
export const repeatShare = (repeat: number, bookings: number): number => (bookings > 0 ? Math.round((repeat / bookings) * 100) : 0);

const pct = (value: number) => `${formatInt(value)} %`;

export const RepeatTile: FC<Ctx> = ({data, compare}) => {
    const {bookings, repeatBookings} = data.current;
    const share = repeatShare(repeatBookings, bookings);
    const previousShare = repeatShare(data.previous.repeatBookings, data.previous.bookings);
    const compareData = hasCompareData(data);

    return (
        <>
            <div className={css.heroRow}>
                <Hero
                    value={bookings > 0 ? formatInt(share) : "–"}
                    unit={bookings > 0 ? "%" : undefined}
                    label={bookings > 0 ? "der Buchungen von Kindern, die schon einmal gebucht haben" : "Keine Buchungen im Zeitraum"}
                    // Prozentpunkte, nicht Prozent vom Anteil: 50 → 55 heißt +5
                    delta={bookings > 0 ? <Delta value={data.previous.bookings > 0 ? share - previousShare : null} unit=" Pkt." noCompare={!compareData} /> : undefined}
                />
            </div>
            <div className={css.statRow}>
                <Stat label="Wiederbucher" value={formatInt(repeatBookings)} delta={<Delta value={pctChange(repeatBookings, data.previous.repeatBookings)} unit=" %" noCompare={!compareData} />} />
                <Stat
                    label="Erstbucher"
                    value={formatInt(bookings - repeatBookings)}
                    delta={<Delta value={pctChange(bookings - repeatBookings, data.previous.bookings - data.previous.repeatBookings)} unit=" %" noCompare={!compareData} />}
                />
            </div>
            <div className={css.legend}>
                <span>
                    <span className={css.legendKey} style={{background: ACCENT}} />
                    {"Anteil Wiederbucher je Monat"}
                </span>
                <span>
                    <span className={css.legendKey} style={{background: COMPARE_COLOR}} />
                    {"Vorjahr"}
                </span>
            </div>
            <div className={css.chart}>
                <LineChart series={data.repeatSeries} dataFrom={data.dataFrom} format={pct} axis={(v) => String(Math.round(v))} max={100} label="Anteil Wiederbucher je Monat, aktuell gegen Vorjahr" />
            </div>
            <div className={css.hint}>
                <span className={css.hintDot} style={{background: "rgba(0,0,0,0.25)"}} />
                <span>
                    {"Wiederbucher: das Kind hatte vorher schon einen anderen Kurs gebucht. Veränderung des Anteils in Prozentpunkten gegenüber "}
                    {compareLabels[compare]}
                    {"."}
                </span>
            </div>
        </>
    );
};
